import { useQuestReportWorkOrderAdministrativeReport } from '@/core/domain/questReport/WorkOrder/WorkOder.hooks';
import {
  QuestWorkOrderAdministrativeReportFormValues,
  QuestWorkOrderAdministrativeReportRequest,
} from '@/core/domain/questReport/WorkOrder/WorkOrder.types';
import { useReportGenerate } from '@/hooks';
import { FileType } from '@/hooks/useFileLoading';
import Authorities from '@/types/Authority';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import { Checkbox, Col, Form, Modal, ModalProps, Row, Tooltip } from 'antd';
import { useForm } from 'antd/lib/form/Form';

interface Props extends Omit<ModalProps, 'onCancel'> {
  onCancel: VoidFunction;
  workOrderId?: number;
  complementNumber?: number;
  format?: FileType;
  licensePlate?: string;
  workorderNumber?: number;
  complementIdentifier?: string;
}

export default function WorkOrderReportAdministrativeModal({
  onCancel,
  workOrderId,
  complementNumber,
  format,
  licensePlate,
  complementIdentifier,
  ...props
}: Props) {
  const [form] = useForm<QuestWorkOrderAdministrativeReportFormValues>();
  const administrativeReport = useQuestReportWorkOrderAdministrativeReport();

  const hideFinancialValues = Form.useWatch('hideFinancialValues', form);

  const endOfComplement = complementIdentifier?.slice(-2);

  const report = useReportGenerate();

  function handleCancel() {
    onCancel();
    form.resetFields();
  }

  const handleOk = async () => {
    const values = await form.validateFields();

    if (!!workOrderId) {
      const obj: QuestWorkOrderAdministrativeReportRequest = {
        workOrderId,
        complementNumber: complementNumber ?? 0,
        ...values,
      };

      const result = await administrativeReport.mutateAsync(obj);

      const fileName = `relatorio-administrativo${
        complementIdentifier ? `-${complementIdentifier}` : ''
      }${licensePlate ? `-${licensePlate}` : ''}.pdf`;

      if (format === 'PDF') {
        report.downloadFile({
          result,
          fileName,
        });
      } else {
        report.openFile({
          result,
          fileName,
          fileType: 'application/pdf',
        });
      }

      handleCancel();
    }
  };

  return (
    <Modal
      width={800}
      title="Relatório administrativo"
      okText="Gerar relatório"
      confirmLoading={administrativeReport.isLoading}
      zIndex={2000}
      onOk={handleOk}
      onCancel={handleCancel}
      destroyOnClose
      {...props}
    >
      <Form form={form}>
        <Row gutter={[16, 16]}>
          <Col span={24} md={12} xl={8}>
            <Form.Item
              name="includeRefusedParts"
              valuePropName="checked"
              noStyle
            >
              <Checkbox>Recusadas</Checkbox>
            </Form.Item>
          </Col>

          <Col span={24} md={12} xl={8}>
            <Form.Item
              name="includeDeletedParts"
              valuePropName="checked"
              noStyle
            >
              <Checkbox>Excluídas</Checkbox>
            </Form.Item>
          </Col>

          <Col span={24} md={12} xl={8}>
            <Form.Item
              name="showManufacturerName"
              valuePropName="checked"
              noStyle
            >
              <Checkbox>Nome do fabricante</Checkbox>
            </Form.Item>
          </Col>

          <Col span={24} md={12} xl={8}>
            <Form.Item name="showEmployeeName" valuePropName="checked" noStyle>
              <Checkbox>Nome do funcionário</Checkbox>
            </Form.Item>
          </Col>

          {hasPermissions([
            Authorities.MOVIMENTATION_WORKORDER_VIEW_COST_HOUR_ADMINISTRATIVE_BUDGET_REPORTS,
          ]) && (
            <Col span={24} md={12} xl={8}>
              <Tooltip
                title="Ao selecionar essa opção, os campos 'Valor Unitário' e 'Valor Líquido' serão ocultados no relatório."
                zIndex={2001}
              >
                <Form.Item name="hideHourCost" valuePropName="checked" noStyle>
                  <Checkbox>Ocultar detalhes dos valores</Checkbox>
                </Form.Item>
              </Tooltip>
            </Col>
          )}

          <Col span={24} md={12} xl={8}>
            <Form.Item
              name="showCustomerObservation"
              valuePropName="checked"
              noStyle
            >
              <Checkbox>Exibir observações do cliente</Checkbox>
            </Form.Item>
          </Col>

          <Col span={24} md={12} xl={8}>
            <Form.Item name="hideStatusDates" valuePropName="checked" noStyle>
              <Checkbox>Ocultar status e datas</Checkbox>
            </Form.Item>
          </Col>

          <Col span={24} md={12} xl={8}>
            <Form.Item
              name="hideFinancialValues"
              valuePropName="checked"
              noStyle
            >
              <Checkbox>Ocultar valores do orçamento</Checkbox>
            </Form.Item>
          </Col>

          {!hideFinancialValues && (
            <Col span={24} md={12} xl={8}>
              <Form.Item
                name="printDiscountValuesItem"
                valuePropName="checked"
                noStyle
              >
                <Checkbox>Exibir descontos em $</Checkbox>
              </Form.Item>
            </Col>
          )}

          <Col span={24} md={12} xl={8}>
            <Form.Item name="hidePartSummary" valuePropName="checked" noStyle>
              <Checkbox>Ocultar resumo de peças</Checkbox>
            </Form.Item>
          </Col>

          <Col span={24} md={12} xl={8}>
            <Form.Item
              name="hideServiceSummary"
              valuePropName="checked"
              noStyle
            >
              <Checkbox>Ocultar resumo de serviços</Checkbox>
            </Form.Item>
          </Col>

          <Col span={24} md={12} xl={8}>
            <Form.Item name="showLocation" valuePropName="checked" noStyle>
              <Checkbox>Exibir localização da peça</Checkbox>
            </Form.Item>
          </Col>

          {endOfComplement === '00' && (
            <Col span={24} md={12} xl={8}>
              <Form.Item name="hideComplement" valuePropName="checked" noStyle>
                <Checkbox>Ocultar valores de complementos</Checkbox>
              </Form.Item>
            </Col>
          )}

          <Col span={24} md={12} xl={8}>
            <Form.Item
              name="ShowOnlyTotalServiceValues"
              valuePropName="checked"
              noStyle
            >
              <Checkbox>Exibir apenas valor total dos serviços</Checkbox>
            </Form.Item>
          </Col>

          <Col span={24} md={12} xl={8}>
            <Form.Item name="hideCustomerType" valuePropName="checked" noStyle>
              <Checkbox>Ocultar tipo de cliente</Checkbox>
            </Form.Item>
          </Col>

          <Col span={24} md={12} xl={8}>
            <Form.Item
              name="hidePartsAndServicesCode"
              valuePropName="checked"
              noStyle
            >
              <Checkbox>Não exibir códigos de peças e serviços</Checkbox>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}
